/**
 * Deferred Prompts API client.
 *
 * M-X10 (2026-05-22): UI surface for the deferred-prompt button +
 * dropdown in the chat composer. Paths here are relative to
 * VITE_API_BASE_URL (= /api) — apiJson prefixes that.
 */
import { apiJson } from "./client";

export interface DeferredPrompt {
  id: string;
  cardId: string;
  text: string;
  createdAt: string;
  createdBy: string | null;
}

export interface DeferredPromptsListResponse {
  items: DeferredPrompt[];
}

export interface CreateDeferredPromptBody {
  promptText: string;
  /**
   * Client-minted, per-draft stable UUID (2026-07-25 duplicate-defer fix).
   *
   * ONE click of the bookmark used to produce TWO rows: the composer's POST
   * and the 30 s background retry of the same localStorage entry were two
   * independent `create()` calls carrying nothing that tied them together,
   * and the gateway minted a fresh id per call. The key travels with the
   * localStorage entry, so the original POST and every retry of that same
   * entry are recognised server-side as one and the same create.
   *
   * Optional on purpose: the gateway still accepts a keyless body (older
   * bundles, e2e helpers), it just cannot dedupe those.
   */
  idempotencyKey?: string;
}

export interface CreateDeferredPromptResponse {
  prompt: DeferredPrompt;
  /** true when the key matched an existing row and nothing new was created. */
  deduplicated?: boolean;
}

export interface RestoreDeferredPromptBody {
  promptText?: string;
}

export const deferredPromptsApi = {
  list: (cardId: string) =>
    apiJson<DeferredPromptsListResponse>(
      `/cards/${cardId}/deferred-prompts`,
    ),

  create: (cardId: string, body: CreateDeferredPromptBody, principalFence: string) =>
    apiJson<CreateDeferredPromptResponse>(
      `/cards/${cardId}/deferred-prompts`,
      {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
          "X-Chaos-Expected-Principal": principalFence,
        },
      },
    ),

  consume: (id: string, principalFence: string) =>
    apiJson<{ ok: true }>(`/deferred-prompts/${id}`, {
      method: "DELETE",
      headers: { "X-Chaos-Expected-Principal": principalFence },
    }),

  restore: (id: string, body: RestoreDeferredPromptBody | undefined, principalFence: string) =>
    apiJson<{ prompt: DeferredPrompt }>(
      `/deferred-prompts/${id}/restore`,
      {
        method: "POST",
        body: body ? JSON.stringify(body) : undefined,
        headers: {
          ...(body ? { "Content-Type": "application/json" } : {}),
          "X-Chaos-Expected-Principal": principalFence,
        },
      },
    ),
};

export const deferredPromptsKeys = {
  all: ["deferred-prompts"] as const,
  list: (cardId: string) => ["deferred-prompts", cardId] as const,
};
